import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18n-js';

import theme from 'theme';
import { round } from 'utils';
import { UserContext } from 'context';
import { Table } from 'components/UI';
import { Box, Grid } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { SearchField } from 'components/FormFields';
import { useStyles, tableStyles } from './StatisticsCard.styled';

const FleetStatisticsTable = ({
  list,
  params,
  search,
  loading,
  pagination,
  handlePageChange,
  handlePerRowsChange,
}) => {
  const { orgType } = useContext(UserContext);
  const classes = useStyles();

  const columns = [
    {
      name: i18n.t('dashboard.statistics.service_area'),
      selector: 'name',
      cell: (row) => (orgType === 'msOwnerOrg' ? row?.name || '--' : row?.parent?.name || '--'),
      grow: 3,
      omit: orgType === 'plainOrg',
    },
    {
      name: i18n.t('dashboard.statistics.vehicle_group'),
      selector: 'name',
      grow: 3,
      omit: orgType === 'msOwnerOrg',
    },
    {
      name: i18n.t('dashboard.statistics.utilized'),
      center: true,
      maxWidth: '80px',
      cell: (row) => row.utilized || 0,
      selector: 'utilized',
      style: {
        borderLeft: `1px solid ${theme.palette.tables.border}`,
        color: theme.palette.success.main,
        justifyContent: 'center',
      },
    },
    {
      name: i18n.t('dashboard.statistics.online'),
      center: true,
      maxWidth: '80px',
      cell: (row) => {
        return <>{(row.available || 0) + (row.utilized || 0)}</>;
      },
      style: {
        color: theme.palette.success.main,
        justifyContent: 'center',
      },
    },
    {
      name: i18n.t('dashboard.statistics.percentage'),
      center: true,
      maxWidth: '80px',
      cell: (row) => {
        const used = row.utilized || 0;
        const total = (row.available || 0) + (row.utilized || 0);

        if (total === 0) return '-';

        const inUse = (used * 100) / total;

        return `${Number.isInteger(inUse) ? inUse : round(inUse, 0)}%`;
      },
      style: {
        color: theme.palette.success.main,
        justifyContent: 'center',
      },
    },
    {
      name: i18n.t('dashboard.statistics.offline'),
      center: true,
      maxWidth: '80px',
      cell: (row) => row.offline || 0,
      style: {
        borderLeft: `1px solid ${theme.palette.tables.border}`,
        justifyContent: 'center',
      },
    },
    {
      name: i18n.t('dashboard.statistics.total'),
      center: true,
      maxWidth: '80px',
      cell: (row) => (row.available || 0) + (row.utilized || 0) + (row.offline || 0),
      style: {
        justifyContent: 'center',
      },
    },
    {
      name: i18n.t('dashboard.statistics.percentage'),
      center: true,
      maxWidth: '80px',
      cell: (row) => {
        const used = row.offline || 0;
        const total =
          (row.available || 0) + (row.inprogress || 0) + (row.reserved || 0) + (row.offline || 0);

        if (total === 0) return '-';

        const inUse = (used * 100) / total;

        return `${Number.isInteger(inUse) ? inUse : round(inUse, 0)}%`;
      },
      style: {
        justifyContent: 'center',
      },
    },
    {
      name: i18n.t('statistics.in_delivery'),
      center: true,
      maxWidth: '80px',
      cell: (row) => row.in_delivery || 0,
      style: {
        borderLeft: `1px solid ${theme.palette.tables.border}`,
        color: theme.palette.primary.main,
        justifyContent: 'center',
      },
    },
    {
      name: i18n.t('dashboard.statistics.in_progress'),
      center: true,
      maxWidth: '80px',
      cell: (row) => row.in_progress || 0,
      style: {
        color: theme.palette.primary.main,
        justifyContent: 'center',
      },
    },
    {
      name: i18n.t('statistics.in_return'),
      center: true,
      maxWidth: '80px',
      cell: (row) => row.in_return || 0,
      style: {
        color: theme.palette.primary.main,
        justifyContent: 'center',
      },
    },
  ];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box className={classes.subheader}>
          <Grid container spacing={2} direction="row" justifyContent="flex-start" alignItems="flex-end">
            <Grid item xs={12} sm={4} md={3}>
              <Grid container spacing={1} alignItems="flex-end" wrap="nowrap">
                <Grid item>
                  <SearchIcon className={classes.searchIcon} />
                </Grid>
                <Grid item style={{ width: '100%' }}>
                  <SearchField
                    allowClear
                    margin="none"
                    label={
                      orgType === 'msOwnerOrg'
                        ? i18n.t('dashboard.search_service_area')
                        : i18n.t('dashboard.search_vehicle_group')
                    }
                    field={{
                      onChange: search,
                      name: 'keyword',
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Table
          data={list}
          columns={columns}
          loading={loading}
          extraStyles={tableStyles}
          handlePageChange={handlePageChange}
          totalRows={pagination?.totalRows || 0}
          handlePerRowsChange={handlePerRowsChange}
        />
      </Grid>
    </Grid>
  );
};

FleetStatisticsTable.propTypes = {
  list: PropTypes.array.isRequired,
  search: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  pagination: PropTypes.object.isRequired,
  handlePageChange: PropTypes.func.isRequired,
  handlePerRowsChange: PropTypes.func.isRequired,
};

FleetStatisticsTable.defaultProps = {
  list: [],
  pagination: {},
  loading: false,
  search: () => {},
  handlePageChange: () => {},
  handlePerRowsChange: () => {},
};

export default FleetStatisticsTable;
